import { useEffect, useState } from 'react';
import { SectionStatusType } from '../data-qa-constants';
import { ConfigSummary, DataQaDataset, MmmConfig, SectionDiffs } from '../data-qa-types';
import { DependentVarSection } from '../sections/DependentVarSection';
import { StageSwitcher } from './StageSwitcher';

export interface DependentVarStage {
  label: string;
  config: MmmConfig;
  dataset: DataQaDataset;
  diffs: SectionDiffs;
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  isColdStart: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
  // Per stage: each stage has its own dependent variable, so one can be blocked by
  // negative values while the other isn't.
  approveBlockedReason?: string;
}

interface DependentVarStageTabsProps {
  stages: DependentVarStage[];
  // Redshift table slug -- threaded through to DependentVarSection's GQV SQL builder.
  company: string;
  // Every model config for the company -- threaded through for the all-models export.
  configs: ConfigSummary[];
  // Reports which stage is on screen -- DataQA.tsx's own "Data is identical across
  // models" banner sits above this component and is otherwise blind to which tab is
  // active, so it can't tell Total Business's own matches from GQV's. Fired on mount
  // (reporting the initial 0) and on every tab click.
  onActiveIndexChange?: (index: number) => void;
}

// Brand Equity's two modeling stages always produce genuinely different
// Dependent Variable data (different target metrics entirely — e.g. GQV vs.
// Total Business) — see DataQA.tsx, which only renders this wrapper when the
// two stages' dependentVar section content hash actually differ. Each tab
// gets its own independent approve/flag, since a reviewer may decide
// differently on one stage's target variable than the other's.
export const DependentVarStageTabs = ({
  stages,
  onActiveIndexChange,
  company,
  configs
}: DependentVarStageTabsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = stages[activeIndex];

  useEffect(() => {
    onActiveIndexChange?.(activeIndex);
  }, [activeIndex, onActiveIndexChange]);

  return (
    <div className='data-qa-stage-tabs'>
      <StageSwitcher
        prompt='This model has two stages — choose one to review its dependent variable:'
        stages={stages.map(stage => ({
          key: stage.config.configPath,
          label: stage.label,
          status: stage.status
        }))}
        activeIndex={activeIndex}
        onSelect={setActiveIndex}
      />
      <DependentVarSection
        config={active.config}
        dataset={active.dataset}
        diffs={active.diffs}
        status={active.status}
        notes={active.notes}
        pending={active.pending}
        isColdStart={active.isColdStart}
        onApprove={active.onApprove}
        onFlag={active.onFlag}
        onNoteChange={active.onNoteChange}
        company={company}
        configs={configs}
        approveBlockedReason={active.approveBlockedReason}
      />
    </div>
  );
};
